'use client';

import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import {
  getEqualDiffArr,
  getReaderCachePres,
  getToFix2NumArr,
  setReaderCachePres,
} from '@/app/_lens-components/utils';
import Button from '@/components/Button';
import Picker from '@/components/Picker';
import Popup from '@/components/Popup';
import { useReporting } from '@/hooks/useReporting';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { formatPrescriptionDecimalForDisplay } from '@/utils/number-format';
import { useTranslations } from '@/i18n/use-translations';
import { useLocale } from 'next-intl';
import { useEffect, useState } from 'react';

interface ReadingPrescriptionPopProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (prescription: { od_sph: string; os_sph: string; presType: string }) => void;
}

export default function ReadingPrescriptionPop({
  open,
  onClose,
  onConfirm,
}: ReadingPrescriptionPopProps) {
  const t = useTranslations('lens-process');
  const locale = useLocale();
  const [defaultIndex, setDefaultIndex] = useState(0);
  const [pickerReady, setPickerReady] = useState(false);
  const { trackEvent } = useReporting();
  const { lensGoodsSensorsData } = useLensProcessStore();

  // 获取默认索引
  const getDefaultIndex = (val: string) => {
    if (!val) {
      val = '+0.25';
    }
    const index = getToFix2NumArr(getEqualDiffArr(0.25, 6, 0.25)).findIndex(
      (item) => item.slice(1) === val,
    );
    setDefaultIndex(index >= 0 ? index : 0);
  };

  // 处理确认
  const handleConfirm = (value: string) => {
    const val = value.slice(1);
    const prescription = {
      od_sph: val,
      os_sph: val,
      presType: EPrescriptionType.READERS,
    };

    setReaderCachePres(prescription);
    onConfirm(prescription);

    try {
      trackEvent(
        {
          eventName: 'PrescriptionEnterPage_PrescriptionIntroduction_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type: EPrescriptionType.READING_GLASSES,
            prescription_type_name: EPrescriptionType.READERS,
            user_prescription_detail: JSON.stringify(prescription),
            btn_name: 'Confirm',
            is_magnification_page: true,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('handleConfirm-trackEvent', error);
    }
  };

  // 处理取消
  const handleCancel = () => {
    onClose();

    // 发送埋点
    trackEvent(
      {
        eventName: 'PrescriptionEnterPage_PrescriptionIntroduction_Click',
        properties: {
          ...(lensGoodsSensorsData || {}),
          prescription_type: EPrescriptionType.READING_GLASSES,
          prescription_type_name: EPrescriptionType.READERS,
          user_prescription_detail: JSON.stringify({}),
          btn_name: 'Cancel',
          is_magnification_page: true,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  useEffect(() => {
    if (open) {
      const sph = getReaderCachePres();
      getDefaultIndex(sph);
      setPickerReady(true);

      // 页面浏览埋点
      trackEvent(
        {
          eventName: 'PrescriptionEnterPage_View',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type: EPrescriptionType.READING_GLASSES,
            prescription_type_name: EPrescriptionType.READERS,
            is_magnification_page: true,
            is_have_scan_prescription: false,
          },
        },
        [ProviderType.SENSORS],
      );

      trackEvent(
        {
          eventName: 'PrescriptionEnterPage_ParamValue_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type: EPrescriptionType.READING_GLASSES,
            prescription_type_name: EPrescriptionType.READERS,
            param_value_type: 'od_sph',
            is_magnification_page: true,
          },
        },
        [ProviderType.SENSORS],
      );
    } else {
      setPickerReady(false);
    }
  }, [open]);

  // 生成选项列表
  const options = getToFix2NumArr(getEqualDiffArr(0.25, 6, 0.25)).map((item) => ({
    label: formatPrescriptionDecimalForDisplay(item, locale),
    value: item,
  }));

  return (
    <Popup
      open={open}
      onClose={handleCancel}
      width="100%"
      title={t('readersLens')}
      hideCloseIcon
      sx={{
        style: {
          borderRadius: '20px 20px 0 0',
          position: 'absolute',
          bottom: 0,
          margin: 0,
          width: '100%',
        },
      }}
      bodyClassName="!px-0"
    >
      {pickerReady && (
        <Picker
          options={options}
          ariaLabel={t('readersLens')}
          defaultValue={options[defaultIndex]?.label}
          visibleOptionNum={7}
          className="mb-[69px]"
          onChange={(value, option, currentIndex) => {
            setDefaultIndex(currentIndex);
            logger.log(currentIndex);
          }}
        />
      )}
      <div className="fixed bottom-0 left-0 right-0 flex justify-between items-center px-[20px] py-[15px] shadow-[0px_-2px_10px_0px_rgba(204,204,204,0.5)]">
        <Button className="" variant="outlined" fullWidth onClick={handleCancel}>
          {t('cancel')}
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={() => handleConfirm(options[defaultIndex]?.value)}
          sx={{
            marginLeft: '15px',
          }}
        >
          {t('confirm')}
        </Button>
      </div>
    </Popup>
  );
}
